import { useEffect, useState } from 'react'
import { Navigate } from 'react-router'
import { Mail } from 'lucide-react'
import { AuthCard } from './AuthCard'
import { SessionUnavailable } from './SessionUnavailable'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Button } from '../../platform/primitives/Button'
import { formatCooldown } from '../../platform/formatCooldown'
import { emit } from '../../platform/analytics/analytics'
import { analyticsCode, authErrorFor, type FieldError } from '../../auth/errors'
import { AuthFailure } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import { FormError } from '../../auth/FormError'
import './auth.css'

/**
 * How long a press of Resend disables the button for.
 *
 * Not sourced from the design: `verify.html`'s "Resend in 0:24" is a
 * mid-countdown snapshot of *some* duration, not the duration itself, and
 * neither the PRD nor the M1 spec names one. 30s is a conservative default —
 * comfortably inside Firebase's own throttle window on `sendEmailVerification`
 * — kept as a named constant rather than a literal so a real number, if one
 * ever gets specified, is a one-line change.
 */
// Exported so the test can derive its expectations from the same number
// rather than restating it as a second, driftable literal.
export const COOLDOWN_SECONDS = 30

/** What the last Resend did. `null` is "not asked yet". Mirrors `VerifyEmailBanner`'s `Resend`. */
type Resend = { ok: true } | { ok: false; error: FieldError } | null

/**
 * M1's post-signup landing, `/verify-email` — "Check your inbox".
 *
 * **The comp draws a "Paste the code" box and a Continue button that cannot be
 * built, and both are omitted rather than built non-functional.** Firebase
 * email verification is link-only: `sendEmailVerification` mails a link
 * carrying an `oobCode` that the SDK spends when the link itself is opened,
 * and `AuthPort` (`../../auth/port.ts`) exposes exactly one operation for this
 * screen — `sendVerificationEmail()`. There is no `verifyEmailCode(...)` to
 * call and no reason to invent one just to give a text box somewhere to go.
 * What is left is what the port can actually do: say where the link went, and
 * offer to send it again.
 *
 * **Not a gate** — same M1-R4 as `VerifyEmailBanner` — this is the screen a
 * signup flow can land on right after `SignUp.tsx`'s fire-and-forget
 * `sendVerificationEmail()` call, not something that blocks the rest of the
 * app. Reaching it with nothing signed in (typed URL, expired session) has
 * nothing to show, so it bounces to `/signin` rather than rendering a
 * placeholder address.
 */
export function VerifyEmail() {
  const auth = useAuth()
  const [busy, setBusy] = useState(false)
  const [resend, setResend] = useState<Resend>(null)
  /**
   * Starts COLD, and this is a correction to the first version.
   *
   * That version started at `COOLDOWN_SECONDS` on the premise that "this screen
   * is reached only after something already sent a link". That premise is not
   * true: nothing navigates here today — `SignUp` lands the user in the app
   * with `VerifyEmailBanner` — and even once something does, the banner is a
   * second entrance that can be taken days later. A user arriving from it would
   * meet a countdown for an email sent long ago, or never.
   *
   * So a hot start asserts "we sent one thirty seconds ago", which this screen
   * does not know. That is the same defect as a lockout counting down to a
   * moment Firebase never told us, and the same answer applies: do not display
   * a figure we cannot justify. The cooldown is set by `onResend` and by
   * nothing else, so whenever it IS showing, it means exactly what it says.
   *
   * Nothing is lost by trusting Firebase here. It throttles abusive resends
   * itself and answers `auth/too-many-requests`, which `KNOWN_CODES` maps to a
   * sentence the user can act on — a real limit, reported honestly, instead of
   * a guessed one enforced locally.
   *
   * A side effect worth recording: a disabled control on the first frame was
   * also failing `compositeContrast`'s sweep, which applies the 4.5:1 floor to
   * `--control-ink-dim` on the disabled fill (4.25 light / 4.48 dark). WCAG
   * 1.4.3 exempts inactive components and that sweep does not model it — a real
   * gap in the guard, filed rather than papered over here, because this screen
   * should not have had a disabled button on mount in the first place.
   */
  const [cooldown, setCooldown] = useState(0)

  /**
   * One interval for the component's whole life, not one per cooldown.
   *
   * A later resend just writes a bigger number into `cooldown` (see
   * `onResend`'s `finally`) — the same tick keeps counting whatever is there
   * down, so there is nothing to tear down and recreate. Cleared on unmount,
   * the same shape `MfaChallenge.tsx`'s own timer takes.
   */
  useEffect(() => {
    const id = window.setInterval(() => {
      setCooldown((seconds) => (seconds > 0 ? seconds - 1 : 0))
    }, 1000)
    return () => window.clearInterval(id)
  }, [])

  // `unknown` is the state on every reload before Firebase reports — not
  // `signedOut`, or a returning user bounces to /signin for a frame.
  if (auth.status === 'unknown') {
    return (
      <AuthCard
        icon={
          <span className="auth-icon">
            <Mail size={24} strokeWidth={1.9} aria-hidden="true" />
          </span>
        }
        title="Check your inbox"
        centered
      >
        <p className="auth-card__note" role="status">
          Checking your sign-in…
        </p>
      </AuthCard>
    )
  }

  // Reached by typing the URL with nobody signed in. There is no address to
  // show and nothing this screen's own controls could do about it.
  if (auth.status === 'signedOut') return <Navigate to="/signin" replace />

  // A second factor is outstanding; nothing on this screen resolves it.
  if (auth.status === 'mfaRequired') return <Navigate to="/mfa" replace />

  // Firebase says yes, our backend did not answer — the same screen
  // `RequireAuth` renders for the same reason.
  if (auth.status === 'sessionUnavailable') return <SessionUnavailable />

  // `bootstrapping` and `signedIn` both carry `user`, and Resend does not
  // depend on the app session existing — only on Firebase's own user.
  const email = auth.user.email

  async function onResend() {
    setBusy(true)
    setResend(null)
    try {
      await auth.sendVerificationEmail()
      emit('email_verification_resent', { error_code: null })
      setResend({ ok: true })
    } catch (thrown) {
      // `auth/too-many-requests` is the expected failure here — Firebase
      // throttles verification mail hard — and reporting it distinguishably is
      // exactly what `VerifyEmailBanner`'s own Resend does.
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      setResend({ ok: false, error: detail })
      emit('email_verification_resent', {
        error_code: analyticsCode(detail.code),
      })
    } finally {
      // Restarts regardless of outcome: a failed send is still a request
      // Firebase just saw, and hammering it again is exactly how
      // `auth/too-many-requests` gets earned.
      setCooldown(COOLDOWN_SECONDS)
      setBusy(false)
    }
  }

  return (
    <AuthCard
      title="Check your inbox"
      centered
      foot="Check spam before asking us — bank-adjacent mail gets filtered more than most."
    >
      <p className="auth-card__note" role="status">
        {/* The address, real data from the signed-in user rather than a
            placeholder — it is what makes this sentence useful to someone who
            has more than one address to check. */}
        We sent a link to <strong>{email ?? 'your email address'}</strong>. Open it on this device
        to verify your address.
      </p>
      {/* A DIRECT child of the card, not `.auth-actions` — that wrapper's
          `align-items: center` sizes a flex child to fit-content, which would
          shrink this button to its label width while every other pre-login
          primary (Sign in, Create account, Verify) stays full-width. The
          card's own flex column leaves a direct child at the default
          `stretch`, which is what `.auth-card--centered`'s own docblock
          argues for the same reason — `text-align: center` centres the
          WORDS, not the box. `.auth-actions` still owns the two secondary
          items below, which the design does draw at content width. */}
      <Button
        variant="primary"
        size="form"
        disabled={busy || cooldown > 0}
        onClick={() => void onResend()}
      >
        {busy
          ? 'Sending…'
          : cooldown > 0
            ? `Resend in ${formatCooldown(cooldown)}`
            : 'Resend email'}
      </Button>
      <div className="auth-actions">
        {resend?.ok === true && (
          <p className="auth-card__note" role="status">
            Verification email sent. It can take a minute — check your spam folder too.
          </p>
        )}
        {resend?.ok === false && <FormError error={resend.error} />}
        <InlineLink to="/signup">Use a different email address</InlineLink>
      </div>
    </AuthCard>
  )
}
